import { Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { submitMeetingReschedule } from "../../../../api/invitations";
import AppDrawer from "../../../../components/drawer";
import { AppAlert } from "../../../../components/form";
import { ClampedTextReadMore } from "../../../../components/form/label/ClampedTextReadMore";
import { SaveAndCancelButton } from "../../../../components/form/buttons";
import { FormHelperTextError } from "../../../../components/form/errors";
import FlexBox from "../../../../components/layouts/Flexbox";
import { RadioOptionSelection } from "../../../../components/form/radiobutton";
import { WizardStepProgress } from "../../../../components/wizard-step-progress";
import {
  MEETING_RESCHEDULE_REASONS,
  type MeetingRescheduleReason,
} from "../../../../constants";
import { ENUM_PROJECT_STATUS, ENUM_RESCHEDULE_REASON_TYPE } from "../../../../enums";
import { isDateTimeBefore } from "../../../../services/utils/date-and-time.utils";
import { useAppDispatch } from "../../../../store";
import { VendorSlot } from "../../vendor-slot-selection/types";
import { MeetingRescheduleSlotsStep } from "./MeetingRescheduleSlotsStep";
import { useMeetingRescheduleDrawerStyles } from "./meeting-reschedule-drawer.styles";
import {
  buildMeetingReschedulePayload,
  validateAmbassadorSlots,
} from "./utils";

const RESCHEDULE_TOTAL_STEPS = 2;

type MeetingRescheduleDrawerProps = {
  open: boolean;
  onClose: () => void;
  invitationId?: string;
  clientTimezoneValue?: string;
  onSuccess?: () => void;
  projectDetails?: any;
};

export function MeetingRescheduleDrawer({
  open,
  onClose,
  invitationId,
  clientTimezoneValue,
  onSuccess,
  projectDetails,
}: MeetingRescheduleDrawerProps) {
  const classes = useMeetingRescheduleDrawerStyles();
  const dispatch = useAppDispatch();

  const [reason, setReason] = useState<MeetingRescheduleReason | "">("");
  const [otherReason, setOtherReason] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [reasonStepError, setReasonStepError] = useState("");
  const [manualSubmitClicked, setManualSubmitClicked] = useState(false);
  const [hasValidSlots, setHasValidSlots] = useState(false);
  const [slots, setSlots] = useState<VendorSlot[]>([]);
  const [slotsStepError, setSlotsStepError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const rescheduleError = useMemo(() => {
    if (!projectDetails) return "";
    if (projectDetails.status !== ENUM_PROJECT_STATUS.SEND_MEETING_LINK) {
      return "Unable to proceed because the meeting is not scheduled.";
    }
    if (
      projectDetails.selectedTimeSlot?.from &&
      isDateTimeBefore(projectDetails.selectedTimeSlot.from)
    ) {
      return "Rescheduling is not allowed for past meetings.";
    }
    return "";
  }, [projectDetails]);

  const selectedReasonLabel = useMemo(() => {
    if (!reason) return "";

    if (reason === ENUM_RESCHEDULE_REASON_TYPE.OTHER) {
      return otherReason.trim() || "Other";
    }

    return (
      MEETING_RESCHEDULE_REASONS.find((option) => option.value === reason)
        ?.label ?? ""
    );
  }, [reason, otherReason]);

  const resetForm = useCallback(() => {
    setReason("");
    setOtherReason("");
    setActiveStep(0);
    setReasonStepError("");
    setManualSubmitClicked(false);
    setHasValidSlots(false);
    setSlots([]);
    setSlotsStepError("");
    setIsSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  const isReasonStepValid =
    Boolean(reason) &&
    (reason !== ENUM_RESCHEDULE_REASON_TYPE.OTHER || Boolean(otherReason.trim()));

  const isSubmitDisabled = !hasValidSlots || isSubmitting;

  const handleNextStep = () => {
    if (!reason) {
      setReasonStepError("Please select a reason");
      return;
    }

    if (reason === ENUM_RESCHEDULE_REASON_TYPE.OTHER && !otherReason.trim()) {
      setReasonStepError("Please provide a reason");
      return;
    }

    setReasonStepError("");
    setActiveStep(1);
  };

  const handleBackStep = () => {
    setActiveStep(0);
    setManualSubmitClicked(false);
    setHasValidSlots(false);
    setSlotsStepError("");
  };

  const handleSubmit = async () => {
    if (!invitationId) {
      setSlotsStepError("Unable to submit reschedule request. Please try again.");
      return;
    }

    setManualSubmitClicked(true);

    const validation = validateAmbassadorSlots({ slots });
    if (!validation.isFormValid) {
      setSlotsStepError(validation.formMessage);
      return;
    }

    setSlotsStepError("");
    setIsSubmitting(true);

    const payload = buildMeetingReschedulePayload({
      reason: reason as MeetingRescheduleReason,
      otherReason,
      slots: validation.updatedList.slots,
      clientTimezoneValue,
    });

    const response = await dispatch(
      submitMeetingReschedule(payload, invitationId)
    );

    setIsSubmitting(false);

    if (response?.success) {
      onSuccess?.();
      onClose();
    }
  };

  const handleSlotsValidityChange = useCallback((isValid: boolean) => {
    setHasValidSlots(isValid);
    if (isValid) {
      setSlotsStepError("");
    }
  }, []);

  const handleSlotsChange = useCallback((nextSlots: VendorSlot[]) => {
    setSlots(nextSlots);
  }, []);

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      title={
        activeStep === 0 ? "Reschedule meeting" : "Suggest alternative times"
      }
      footer={
        rescheduleError ? null : (
          <FlexBox justifyContent="flex-end">
            <SaveAndCancelButton
              primaryButtonText={
                activeStep === 0 ? "Next" : "Submit request"
              }
              secondaryButtonText={activeStep === 1 ? "Back" : "Cancel"}
              onActionButtonClick={
                activeStep === 0 ? handleNextStep : handleSubmit
              }
              onCancelButtonClick={
                activeStep === 1 ? handleBackStep : onClose
              }
              loading={isSubmitting}
              primaryButtonProps={{
                type: "button",
                fullWidth: false,
                disabled: activeStep === 0 ? !isReasonStepValid : isSubmitDisabled,
                className:
                  activeStep === 0
                    ? classes.footerButtonNext
                    : classes.footerButtonSubmit,
              }}
              secondaryButtonProps={{
                fullWidth: false,
                disabled: isSubmitting,
                className: classes.footerButtonSecondary,
              }}
            />
          </FlexBox>
        )
      }
    >
      <Stack gap={3}>
        {rescheduleError ? (
          <AppAlert severity="error">{rescheduleError}</AppAlert>
        ) : (
          <>
            <WizardStepProgress
              totalSteps={RESCHEDULE_TOTAL_STEPS}
              activeStep={activeStep}
            />

            {activeStep === 1 && selectedReasonLabel && (
              <Stack gap={0.25} className={classes.reasonSummary}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Reason
                </Typography>
                <ClampedTextReadMore text={selectedReasonLabel} />
              </Stack>
            )}

            {activeStep === 0 ? (
              <Stack gap={2}>
                {reasonStepError && (
                  <FormHelperTextError showIcon error={reasonStepError} />
                )}
                <RadioOptionSelection
                  options={MEETING_RESCHEDULE_REASONS}
                  value={reason}
                  onChange={(value) => {
                    setReason(value);
                    setReasonStepError("");
                    if (value !== ENUM_RESCHEDULE_REASON_TYPE.OTHER) {
                      setOtherReason("");
                    }
                  }}
                  otherValue={otherReason}
                  onOtherValueChange={(value) => {
                    setOtherReason(value);
                    setReasonStepError("");
                  }}
                  otherOptionValue={ENUM_RESCHEDULE_REASON_TYPE.OTHER}
                  note={
                    <AppAlert severity="info" className={classes.noteAlert}>
                      Please note that the reason will be shared with the vendor.
                    </AppAlert>
                  }
                  otherFieldPlaceholder="Briefly describe your reason for rescheduling"
                  otherFieldId="reschedule-other-reason"
                />
              </Stack>
            ) : (
              <Stack gap={2}>
                {slotsStepError && (
                  <FormHelperTextError showIcon error={slotsStepError} />
                )}
                <MeetingRescheduleSlotsStep
                  clientTimezoneValue={clientTimezoneValue}
                  showErrors={manualSubmitClicked}
                  onValidityChange={handleSlotsValidityChange}
                  onSlotsChange={handleSlotsChange}
                />
              </Stack>
            )}
          </>
        )}
      </Stack>
    </AppDrawer>
  );
}
